/* =========================================================
   HERE & ALWAYS — Wrapped PRODUCT-Level PDP Launcher V2.4

   APP OWNERSHIP: style the NATIVE Wrapped Dialog launcher in
   its own Shadow DOM. Theme CSS handles the adjacent 50/50 layout.

   Safe guards:
   - Applies ONLY inside .ha-pdp-hero-v2
   - Applies ONLY to product-page, option-type=product
   - Requires actual visible native Dialog launcher + ATC
   - Does NOT click, clone, detach, or mutate form inputs
   - If gifting is unavailable, leaves original full-width ATC
   - Does NOT target the cart-level Wrapped UUID/component
   ========================================================= */
(function () {
  'use strict';
  if (window.__haWrappedPdpSplitV24) return;
  window.__haWrappedPdpSplitV24 = true;

  const STYLE_ID = 'ha-wrapped-pdp-launcher-v24';
  const READY_CLASS = 'ha-pdp-purchase-split';
  const installedForms = new WeakSet();

  // Deliberately isolated from cart-level Wrapped styling.
  const shadowCSS = `
    :host {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      height: 100% !important;
      min-height: 50px !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
    }

    gift-block-launcher {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
    }

    gift-block-launcher button.gift-block__launcher {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      min-height: 50px !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 4px !important;
      background: #fff !important;
      color: rgba(0,0,0,.55) !important;
      border: 1px solid rgba(0,0,0,.18) !important;
      border-right: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      font-family: "Bicyclette", Arial, sans-serif !important;
      font-size: 11px !important;
      font-weight: 400 !important;
      line-height: 1.25 !important;
      letter-spacing: .055em !important;
      text-transform: uppercase !important;
      text-align: center !important;
      white-space: nowrap !important;
      cursor: pointer !important;
      transition: border-color 160ms ease, color 160ms ease !important;
    }

    gift-block-launcher button.gift-block__launcher .button-content {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      flex-direction: row !important;
      width: 100% !important;
      min-width: 0 !important;
      gap: 0 !important;
      white-space: nowrap !important;
    }

    gift-block-launcher button.gift-block__launcher .label {
      display: block !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      color: inherit !important;
      font-family: "Bicyclette", Arial, sans-serif !important;
      font-size: 11px !important;
      font-weight: 400 !important;
      line-height: 1.25 !important;
      letter-spacing: .055em !important;
      text-transform: uppercase !important;
      text-align: center !important;
      white-space: nowrap !important;
    }

    gift-block-launcher button.gift-block__launcher .icon-wrapper {
      display: none !important;
    }

    @media (hover: hover) {
      gift-block-launcher button.gift-block__launcher:hover {
        background: #fff !important;
        color: #111 !important;
        border-color: #111 !important;
        border-right: 0 !important;
        box-shadow: none !important;
      }
    }

    gift-block-launcher button.gift-block__launcher:focus-visible {
      outline: 1px solid #111 !important;
      outline-offset: 3px !important;
    }

    gift-block-launcher button.gift-block__launcher:disabled {
      opacity: .5 !important;
      cursor: not-allowed !important;
    }

    @media (max-width: 767px) {
      gift-block-launcher button.gift-block__launcher,
      gift-block-launcher button.gift-block__launcher .label {
        font-size: 10px !important;
        letter-spacing: .035em !important;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      gift-block-launcher button.gift-block__launcher {
        transition: none !important;
      }
    }
  `;

  function start(form) {
    if (installedForms.has(form)) return;
    installedForms.add(form);

    let watchedHost = null;
    let shadowObserver = null;
    let frame = 0;

    function schedule() {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    }

    function sync() {
      if (!form.isConnected) {
        shadowObserver?.disconnect();
        form.classList.remove(READY_CLASS);
        return;
      }

      const children = Array.from(form.children);
      const gift = children.find(el => el.matches(
        'wrapped-gift-block[option-type="product"][target-group="product-page"]'
      ));
      const cart = children.find(el =>
        el.classList.contains('product-add-to-cart-container')
      );

      if (gift !== watchedHost || (gift?.shadowRoot && !shadowObserver)) {
        shadowObserver?.disconnect();
        watchedHost = gift || null;
        shadowObserver = null;
        if (gift?.shadowRoot) {
          shadowObserver = new MutationObserver(schedule);
          shadowObserver.observe(gift.shadowRoot, {
            childList: true,
            subtree: true
          });
        }
      }

      const shadow = gift?.shadowRoot;
      const launcher = shadow && Array.from(
        shadow.querySelectorAll('gift-block-launcher button.gift-block__launcher')
      ).find(button =>
        !button.disabled &&
        !button.closest('[inert]') &&
        button.getClientRects().length > 0
      );

      const dialog = shadow?.querySelector('dialog.gift-block-modal');
      const giftIndex = children.indexOf(gift);
      const cartIndex = children.indexOf(cart);

      const between = giftIndex >= 0 && cartIndex > giftIndex
        ? children.slice(giftIndex + 1, cartIndex)
        : [];

      // Do not reorder important controls that a future app inserts.
      const unexpectedVisible = between.some(el => {
        const styles = getComputedStyle(el);
        return styles.display !== 'none' &&
          styles.visibility !== 'hidden' &&
          el.getBoundingClientRect().height > 1;
      });

      const giftVisible = !!gift &&
        getComputedStyle(gift).display !== 'none' &&
        getComputedStyle(gift).visibility !== 'hidden';

      const ready = !!(
        gift && cart && giftIndex >= 0 && cartIndex > giftIndex &&
        giftVisible && launcher && dialog && !unexpectedVisible &&
        cart.querySelector('button#AddToCart.single-add-to-cart-button')
      );

      // Style only a confirmed product-level native Wrapped launcher.
      if (ready && !shadow.getElementById(STYLE_ID)) {
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = shadowCSS;
        shadow.appendChild(style);
      }

      form.classList.toggle(READY_CLASS, ready);
    }

    const formObserver = new MutationObserver(schedule);
    formObserver.observe(form, { childList: true });

    sync();
    // Bounded readiness checks for asynchronously attached Shadow DOM.
    [100, 350, 900, 1800].forEach(delay => setTimeout(schedule, delay));
  }

  function init(scope = document) {
    scope.querySelectorAll('.ha-pdp-hero-v2 form').forEach(form => {
      if (form.querySelector('.product-add-to-cart-container')) start(form);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', event => init(event.target));
})();